import create from 'zustand';
import type { Subscription } from '@rails/actioncable';
import consumer from '@/channels/consumer';
import { logLiveSessionEvent } from '@/modules/liveSessionEvents';
import type {
  JoinNotificationMessage,
  StudentLiveSessionMessage,
  StudentRemovedMessage,
} from './messages';

// Module-scoped store: one StudentLiveSessionChannel subscription per page, refcounted
// across React trees so the layout-level listener and PresentMode share one underlying
// `student_ls:{user_id}` subscription instead of opening parallel ones.

interface StudentLiveSessionState {
  activeSessionId: string | null;
  joinNotification: JoinNotificationMessage | null;
  removalNotification: StudentRemovedMessage | null;
}

interface StudentLiveSessionActions {
  setActiveSessionId: (sessionId: string | null) => void;
  attach: () => () => void;
}

interface InternalState extends StudentLiveSessionState, StudentLiveSessionActions {
  refCount: number;
  subscription: Subscription | null;
}

const isJoinNotificationValid = (data: JoinNotificationMessage): boolean => (
  Boolean(data.session_id)
  && data.slide_show_id != null
  && data.program_id != null
  && data.parent_id != null
  && data.parent_type != null
);

const useStudentLiveSessionStore = create<InternalState>((set, get) => ({
  activeSessionId: null,
  joinNotification: null,
  removalNotification: null,
  refCount: 0,
  subscription: null,
  setActiveSessionId: activeSessionId => set({ activeSessionId }),
  attach: () => {
    const nextCount = get().refCount + 1;
    set({ refCount: nextCount });

    if (nextCount === 1) {
      const subscription = consumer.subscriptions.create(
        { channel: 'StudentLiveSessionChannel' },
        {
          received(data: StudentLiveSessionMessage) {
            if (data?.type === 'student_removed') {
              set({
                activeSessionId: null,
                joinNotification: null,
                removalNotification: data,
              });
              logLiveSessionEvent(data.session_id, 'student_removed_received');
              return;
            }
            if (data?.type !== 'join_notification') return;
            if (!isJoinNotificationValid(data)) {
              logLiveSessionEvent(data.session_id, 'join_notification_suppressed', { reason: 'invalid_payload' });
              return;
            }

            set({
              activeSessionId: data.session_id ?? null,
              joinNotification: data,
            });
          },
        },
      );
      set({ subscription });
    }

    return () => {
      const remaining = get().refCount - 1;
      set({ refCount: remaining });

      if (remaining === 0) {
        get().subscription?.unsubscribe();
        set({ subscription: null });
      }
    };
  },
}));

export default useStudentLiveSessionStore;
